'use client'

import { type RefCallback, useEffect, useState } from 'react'

/**
 * Slider functionality works only on the client-side. Customization is done using the HTML attribute `hidden`.
 */
export const useSliderNavigation = <T extends HTMLElement>(): {
  containerProps: {
    ref: RefCallback<T>
    tabIndex: 0
  }
  leftRef: RefCallback<HTMLButtonElement>
  rightRef: RefCallback<HTMLButtonElement>
} => {
  const [container, setContainer] = useState<T | null>(null)
  const [leftButton, setLeftButton] = useState<HTMLButtonElement | null>(null)
  const [rightButton, setRightButton] = useState<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (!container) {
      return undefined
    }

    /**
     * If slider items have a gap between, we take this value in runtime into JavaScript
     * to correct the scroll delta value
     */
    const gap = Math.trunc(Number(getComputedStyle(container).gap)) || 0
    const delta = container.offsetWidth + gap

    const scrollLeft = (): void => {
      container.scrollBy({
        left: -delta,
      })
    }

    const scrollRight = (): void => {
      container.scrollBy({
        left: delta,
      })
    }

    const checkVisibility = (): void => {
      // eslint-disable-next-line react-hooks/immutability -- don't reference, refactor if touched
      if (leftButton) {
        leftButton.hidden = container.scrollLeft === 0
      }

      // eslint-disable-next-line react-hooks/immutability -- don't reference, refactor if touched
      if (rightButton) {
        rightButton.hidden =
          Math.round(container.scrollLeft + container.offsetWidth) + 1 >=
          container.scrollWidth
      }
    }

    checkVisibility()

    // eslint-disable-next-line unicorn/prefer-observer-apis -- don't reference, refactor if touched
    container.addEventListener('scroll', checkVisibility, {
      passive: true,
    })

    leftButton?.addEventListener('click', scrollLeft)
    rightButton?.addEventListener('click', scrollRight)

    return (): void => {
      container.removeEventListener('scroll', checkVisibility)
      leftButton?.removeEventListener('click', scrollLeft)
      rightButton?.removeEventListener('click', scrollRight)
    }
  }, [container, leftButton, rightButton])

  return {
    containerProps: {
      ref: setContainer,
      // Scrollable region must have keyboard access https://dequeuniversity.com/rules/axe/4.11/scrollable-region-focusable
      tabIndex: 0,
    },
    leftRef: setLeftButton,
    rightRef: setRightButton,
  }
}
